import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { Card, CardBody } from "@spark/ui-components/components/Card";
import { Button, ButtonWithLoading } from "@spark/ui-components/components/Button";
import { notification } from "@spark/ui-components/utils/notification";
import { getPedido, updatePedido, getRecomendacaoFrotaByPedido } from "@spark/services/pedidos";
import { getVeiculos, relacionarVeiculoPedido } from "@spark/services/frotas";
import { fmt, fmtDate, parseDecimalValue, formatDecimalPtBr, mapFormToCreateApi } from "./utils/formatting";
import { Linha, Bloco } from "./components/InfoDisplay";
import {
  extractOptionId as extractOptionIdUtil,
  normalizeOptionValue as normalizeOptionValueUtil,
  formatVeiculoOption as formatVeiculoOptionUtil,
} from "./utils/vehicle";
import RecommendationSection from "./components/RecommendationSection";
import VehicleSearch from "./components/VehicleSearch";
import SelectedVeiculosPanel from "./components/SelectedVeiculosPanel";
import CapacitySummary from "./components/CapacitySummary";
import useCapacityFeedback from "./hooks/useCapacityFeedback";

const RECOMMENDATION_RULES = [
  {
    id: "peso",
    title: "Peso",
    description:
      'Pedido e modelo precisam ter unidade "Peso" e numeros validos; o pedido nao pode exceder 104% da capacidade do modelo e a capacidade nao pode exceder 104% do peso pedido. Fora dessa banda, regra reprova e zera o score (services/veiculos/recomendacaoFrotaService.js:61-95).',
  },
  {
    id: "cubagem",
    title: "Cubagem",
    description:
      "Cubagem do pedido precisa ficar <= 80% (0.8) da ocupacao cubica maxima do modelo; se passar desse limite, a regra reprova (services/veiculos/recomendacaoFrotaService.js:97-119).",
  },
  {
    id: "pallets",
    title: "Pallets",
    description:
      "Numero de pallets do pedido precisa ser <= CapacidadeMaximoPallets; se faltar dado ou exceder o limite, a regra reprova (services/veiculos/recomendacaoFrotaService.js:121-139).",
  },
  {
    id: "modeloSolicitado",
    title: "Modelo solicitado",
    description:
      "ModeloVeicularSolicitado do pedido deve casar (substring, case-insensitive) com NomeTipoModeloVeicular, Descricao ou CodigoTipoModeloVeicular. Sem correspondencia, a regra reprova (services/veiculos/recomendacaoFrotaService.js:141-175).",
  },
];

const Forms = ({ isReadOnly = false }) => {
  const history = useHistory();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [veiculoOptions, setVeiculoOptions] = useState([]);
  const [selectedVeiculos, setSelectedVeiculos] = useState([]);
  const [pedidoRecomendado, setPedidoRecomendado] = useState(null);
  const [recomendacoes, setRecomendacoes] = useState([]);
  const [loadingRecomendacoes, setLoadingRecomendacoes] = useState(false);
  const [expandedRecommendations, setExpandedRecommendations] = useState(() => new Set());
  const [isRulesModalOpen, setIsRulesModalOpen] = useState(false);
  const initialVeiculosRef = useRef([]);
  const isEditMode = !!id && !isReadOnly;

  const valuesForView =
    initialData || {
      numeroDoPedido: "",
      carga: null,
      localPalletizacao: "",
      dataAgendamento: null,
      numeroDePallets: null,
      pesoLiquido: null,
      tipoDeRemetente: "",
      numeroPedidoEmbarcador: "",
      dataColeta: null,
      remetente: "",
      destinatario: "",
      origem: "",
      destino: "",
      tipoDeCarga: "",
      tipoDeOperacao: "",
      rota: "",
      empresaFilial: "",
      modeloVeicularSolicitado: "",
      dataPrevisaoSaida: null,
      dataPrevisaoEntregaRemetente: null,
      dataInicialViagemExecutada: null,
      dataFinalViagemExecutada: null,
      dataInicialViagemFaturada: null,
      dataFinalViagemFaturada: null,
      tomador: "",
      pesoDoPedido: null,
      quantidadeDeEntrega: null,
      dataAgendamentoPedido: null,
      condicaoDoPedido: "",
      observacaoPedido: "",
    };

  const {
    selectedVeiculosCount,
    capacidadeTotalSelecionada,
    pesoDoPedidoNumber,
    capacityFeedback,
  } = useCapacityFeedback(selectedVeiculos, valuesForView?.pesoDoPedido);

  const extractOptionId = useCallback((opt) => extractOptionIdUtil(opt), []);

  const normalizeOptionValue = useCallback((opt) => normalizeOptionValueUtil(opt), []);

  const isInitialSelection = useCallback(
    (opt) => {
      const normalized = normalizeOptionValue(opt);
      if (normalized === null) return false;
      const initial = initialVeiculosRef.current || [];
      return initial.some((item) => normalizeOptionValue(item) === normalized);
    },
    [normalizeOptionValue]
  );

  const formatVeiculoOption = useCallback((veiculo) => formatVeiculoOptionUtil(veiculo), []);

  const openRulesModal = useCallback(() => setIsRulesModalOpen(true), []);
  const closeRulesModal = useCallback(() => setIsRulesModalOpen(false), []);

  useEffect(() => {
    const loadData = async () => {
      if (!id) return;
      try {
        setLoadingData(true);
        const pedido = await getPedido(id);

        const base = {
          numeroDoPedido: pedido?.NumeroDoPedido || "",
          carga: pedido?.IdCarga
            ? {
              value: pedido.IdCarga,
              label: `${pedido?.NumeroDaCarga || "Carga"} - ${pedido?.Origem || ""} -> ${pedido?.Destino || ""}`,
            }
            : null,
          localPalletizacao: pedido?.LocalPalletizacao || "",
          dataAgendamento: pedido?.DataAgendamento ? new Date(pedido.DataAgendamento) : null,
          numeroDePallets: pedido?.NumeroDePallets ?? null,
          pesoLiquido: pedido?.PesoLiquido ?? null,
          tipoDeRemetente: pedido?.TipoDeRemetente || "",
          numeroPedidoEmbarcador: pedido?.NumeroPedidoEmbarcador || "",
          dataColeta: pedido?.DataColeta ? new Date(pedido.DataColeta) : null,
          remetente: pedido?.Remetente || "",
          destinatario: pedido?.Destinatario || "",
          origem: pedido?.Origem || "",
          destino: pedido?.Destino || "",
          tipoDeCarga: pedido?.TipoDeCarga || "",
          tipoDeOperacao: pedido?.TipoDeOperacao || "",
          rota: pedido?.Rota || "",
          empresaFilial: pedido?.EmpresaFilial || "",
          modeloVeicularSolicitado: pedido?.ModeloVeicularSolicitado || "",
          dataPrevisaoSaida: pedido?.DataPrevisaoSaida ? new Date(pedido.DataPrevisaoSaida) : null,
          dataPrevisaoEntregaRemetente: pedido?.DataPrevisaoEntregaRemetente ? new Date(pedido.DataPrevisaoEntregaRemetente) : null,
          dataInicialViagemExecutada: pedido?.DataInicialViagemExecutada ? new Date(pedido.DataInicialViagemExecutada) : null,
          dataFinalViagemExecutada: pedido?.DataFinalViagemExecutada ? new Date(pedido.DataFinalViagemExecutada) : null,
          dataInicialViagemFaturada: pedido?.DataInicialViagemFaturada ? new Date(pedido.DataInicialViagemFaturada) : null,
          dataFinalViagemFaturada: pedido?.DataFinalViagemFaturada ? new Date(pedido.DataFinalViagemFaturada) : null,
          tomador: pedido?.Tomador || "",
          pesoDoPedido: pedido?.PesoDoPedido ?? null,
          quantidadeDeEntrega: pedido?.QuantidadeDeEntrega ?? null,
          dataAgendamentoPedido: pedido?.DataAgendamentoPedido ? new Date(pedido.DataAgendamentoPedido) : null,
          condicaoDoPedido: pedido?.CondicaoDoPedido || "",
          observacaoPedido: pedido?.ObservacaoPedido || "",
        };
        setInitialData(base);

        // veículos atrelados
        const veiculosDoPedido = Array.isArray(pedido?.veiculos)
          ? (pedido?.veiculos || [])
            .map((v) =>
              formatVeiculoOption({
                ...v,
                Frota: v?.Frota ?? v?.ModeloVeicular?.Descricao ?? "",
              })
            )
            .filter(Boolean)
          : [];

        try {
          const resp = await getVeiculos({ IdPedido: id, limit: 0 });
          const relacionados = (resp?.registros || []).map(formatVeiculoOption).filter(Boolean);
          const map = new Map();
          [...veiculosDoPedido, ...relacionados].forEach((opt) => {
            const normalized = normalizeOptionValue(opt);
            if (normalized) {
              map.set(normalized, opt);
            }
          });
          const selecionados = Array.from(map.values());
          setSelectedVeiculos(selecionados);
          initialVeiculosRef.current = selecionados;
          setVeiculoOptions(selecionados);
        } catch {
          setSelectedVeiculos(veiculosDoPedido);
          initialVeiculosRef.current = veiculosDoPedido;
          setVeiculoOptions(veiculosDoPedido);
        }
      } catch (e) {
        console.error("Erro ao carregar pedido:", e);
      } finally {
        setLoadingData(false);
      }
    };
    loadData();
  }, [id, formatVeiculoOption, normalizeOptionValue]);

  useEffect(() => {
    let ativo = true;

    const carregarRecomendacoes = async () => {
      if (!id) {
        setPedidoRecomendado(null);
        setRecomendacoes([]);
        setLoadingRecomendacoes(false);
        return;
      }

      setLoadingRecomendacoes(true);

      try {
        const resposta = await getRecomendacaoFrotaByPedido(id);
        if (!ativo) {
          return;
        }

        const recomendacoesResposta = Array.isArray(resposta?.recomendacoes) ? resposta.recomendacoes : [];
        const pedidoResposta = resposta?.pedido || null;

        setPedidoRecomendado(pedidoResposta);
        const modelosConsideradosResposta = Array.isArray(resposta?.modelosConsiderados)
          ? resposta.modelosConsiderados
          : [];

        const capacidadeModeloPorModeloId = new Map();
        modelosConsideradosResposta.forEach((modelo) => {
          const idCandidates = [
            modelo?.id,
            modelo?.Id,
            modelo?.idModeloVeicular,
            modelo?.IdModeloVeicular,
          ];
          const foundId = idCandidates.find(
            (candidate) => candidate !== undefined && candidate !== null && candidate !== ""
          );
          if (foundId === undefined || foundId === null) {
            return;
          }
          const parsedCapacidade = parseDecimalValue(
            modelo?.capacidadeModelo ?? modelo?.CapacidadeModelo
          );
          if (parsedCapacidade !== null) {
            capacidadeModeloPorModeloId.set(String(foundId), parsedCapacidade);
          }
        });

        const recomendacoesNormalizadas = recomendacoesResposta.map((rec) => {
          const modeloIdCandidates = [rec?.idModeloVeicular, rec?.IdModeloVeicular];
          const modeloIdFound = modeloIdCandidates.find(
            (candidate) => candidate !== undefined && candidate !== null && candidate !== ""
          );
          const modeloIdKey =
            modeloIdFound !== undefined && modeloIdFound !== null ? String(modeloIdFound) : null;
          const capacidadeRec = parseDecimalValue(rec?.capacidadeModelo ?? rec?.CapacidadeModelo);
          const capacidadeFallback = modeloIdKey
            ? capacidadeModeloPorModeloId.get(modeloIdKey) ?? null
            : null;
          const capacidadeFinal = capacidadeRec ?? capacidadeFallback ?? null;
          return {
            ...rec,
            capacidadeModelo: capacidadeFinal ?? rec?.capacidadeModelo ?? rec?.CapacidadeModelo ?? null,
            CapacidadeModelo: capacidadeFinal ?? rec?.capacidadeModelo ?? rec?.CapacidadeModelo ?? null,
            capacidadeModeloCalculada: capacidadeFinal,
          };
        });

        setRecomendacoes(recomendacoesNormalizadas);

        const opcoesRecomendadas = recomendacoesNormalizadas
          .map((rec) => {
            const modeloIdCandidates = [rec?.idModeloVeicular, rec?.IdModeloVeicular];
            const modeloIdFound = modeloIdCandidates.find(
              (candidate) => candidate !== undefined && candidate !== null && candidate !== ""
            );
            const modeloIdKey =
              modeloIdFound !== undefined && modeloIdFound !== null ? String(modeloIdFound) : null;
            const capacidadeNormalizada = Number.isFinite(rec?.capacidadeModeloCalculada)
              ? rec.capacidadeModeloCalculada
              : parseDecimalValue(rec?.capacidadeModelo ?? rec?.CapacidadeModelo);
            const capacidadeFinal =
              capacidadeNormalizada ?? (modeloIdKey
                ? capacidadeModeloPorModeloId.get(modeloIdKey) ?? null
                : null);

            return formatVeiculoOption({
              Id: rec?.idVeiculo,
              id: rec?.idVeiculo,
              Frota: rec?.frota,
              Placa: rec?.placa,
              CapacidadeModelo: capacidadeFinal,
              capacidadeModelo: capacidadeFinal,
              ModeloVeicular: {
                Descricao: rec?.modeloDescricao,
                CapacidadeModelo: capacidadeFinal,
              },
              modeloVeicular: {
                Descricao: rec?.modeloDescricao,
                CapacidadeModelo: capacidadeFinal,
              },
              IdModeloVeicular: rec?.idModeloVeicular,
              idModeloVeicular: rec?.idModeloVeicular,
            });
          })
          .filter(Boolean);

        if (opcoesRecomendadas.length > 0) {
          setVeiculoOptions((prev = []) => {
            const mapa = new Map();
            (prev || []).forEach((opt) => {
              const normalizedPrev = normalizeOptionValue(opt);
              if (normalizedPrev) {
                mapa.set(normalizedPrev, opt);
              }
            });
            opcoesRecomendadas.forEach((opt) => {
              const normalized = normalizeOptionValue(opt);
              if (normalized) {
                mapa.set(normalized, opt);
              }
            });
            return Array.from(mapa.values());
          });
        }
      } catch (error) {
        if (ativo) {
          console.error("Erro ao carregar recomendações de frota:", error);
        }
      } finally {
        if (ativo) {
          setLoadingRecomendacoes(false);
        }
      }
    };

    carregarRecomendacoes();

    return () => {
      ativo = false;
    };
  }, [id, formatVeiculoOption, normalizeOptionValue]);

  const handleSearchVeiculos = useCallback(
    async (term) => {
      const q = (term || "").trim();
      if (q.length === 0) {
        setVeiculoOptions((prev) => (prev || []).slice(0, 5));
        return;
      }
      try {
        const resp = await getVeiculos({ frota: q, limit: 5 });
        const registros = resp?.registros || [];
        const opcoes = registros.map(formatVeiculoOption).filter(Boolean).slice(0, 5);

        const map = new Map();
        [...(selectedVeiculos || []), ...opcoes].forEach((opt) => {
          const normalized = normalizeOptionValue(opt);
          if (normalized) {
            map.set(normalized, opt);
          }
        });
        setVeiculoOptions(Array.from(map.values()));
      } catch (e) {
        console.error("Erro ao buscar veiculos:", e);
      }
    },
    [formatVeiculoOption, selectedVeiculos, normalizeOptionValue]
  );

  const toggleSelecionado = useCallback(
    async (opt) => {
      if (!opt) return;
      const optionValueNormalized = normalizeOptionValue(opt);
      if (optionValueNormalized === null) return;

      const optionRawId = extractOptionId(opt);
      const pedidoIdNumber = Number(id);
      const idPedidoPayload = Number.isNaN(pedidoIdNumber) ? id : pedidoIdNumber;

      const alreadySelected = (selectedVeiculos || []).some(
        (p) => normalizeOptionValue(p) === optionValueNormalized
      );

      if (alreadySelected) {
        setSelectedVeiculos((prev = []) =>
          prev.filter((p) => normalizeOptionValue(p) !== optionValueNormalized)
        );

        if (isInitialSelection(opt) && id) {
          if (optionRawId === null) {
            return;
          }
          const vehicleIdNumber = Number(optionRawId);
          const vehicleIdParam = Number.isNaN(vehicleIdNumber) ? optionRawId : vehicleIdNumber;
          try {
            await relacionarVeiculoPedido(vehicleIdParam, idPedidoPayload, true);
            initialVeiculosRef.current = (initialVeiculosRef.current || []).filter(
              (item) => normalizeOptionValue(item) !== optionValueNormalized
            );
          } catch (error) {
            console.error("Erro ao desvincular frota do pedido:", error);
            notification.error("Não foi possível desvincular a frota do pedido.");
            setSelectedVeiculos((prev = []) => {
              const exists = prev.some(
                (p) => normalizeOptionValue(p) === optionValueNormalized
              );
              if (exists) {
                return prev;
              }
              return [...prev, opt];
            });
          }
        }
        return;
      }

      setSelectedVeiculos((prev = []) => {
        const exists = prev.some(
          (p) => normalizeOptionValue(p) === optionValueNormalized
        );
        if (exists) {
          return prev;
        }
        return [...prev, opt];
      });
    },
    [selectedVeiculos, normalizeOptionValue, extractOptionId, isInitialSelection, id]
  );

  const recommendationCards = useMemo(() => {
    if (!Array.isArray(recomendacoes) || recomendacoes.length === 0) {
      return [];
    }

    const mapaOpcoes = new Map();
    (veiculoOptions || []).forEach((opt) => {
      const normalized = normalizeOptionValue(opt);
      if (normalized) {
        mapaOpcoes.set(normalized, opt);
      }
    });

  return recomendacoes
      .map((rec, index) => {
        const chave = rec?.idVeiculo !== undefined && rec?.idVeiculo !== null ? String(rec.idVeiculo) : null;
        let option = chave ? mapaOpcoes.get(chave) : null;

        if (!option) {
          option = formatVeiculoOption({
            Id: rec?.idVeiculo,
            id: rec?.idVeiculo,
            Frota: rec?.frota,
            Placa: rec?.placa,
            ModeloVeicular: {
              Descricao: rec?.modeloDescricao,
            },
            modeloVeicular: {
              Descricao: rec?.modeloDescricao,
            },
            IdModeloVeicular: rec?.idModeloVeicular,
            idModeloVeicular: rec?.idModeloVeicular,
          });
        }

        if (!option) {
          return null;
        }

        return {
          dados: rec,
          option,
          fallbackKey: chave ?? `__rec-${index}`,
        };
      })
      .filter(Boolean);
  }, [recomendacoes, veiculoOptions, formatVeiculoOption, normalizeOptionValue, extractOptionId]);

  const selectedRecommendationIds = useMemo(() => {
    const ids = new Set();
    (selectedVeiculos || []).forEach((opt) => {
      const normalized = normalizeOptionValue(opt);
      if (normalized) {
        ids.add(normalized);
      }
    });
    return ids;
  }, [selectedVeiculos, normalizeOptionValue]);

  const handleSelectRecommendation = useCallback(
    (option) => {
      if (!option) return;
      const normalizedValue = normalizeOptionValue(option);

      setSelectedVeiculos((prev = []) => {
        if (prev.some((p) => normalizeOptionValue(p) === normalizedValue)) {
          return prev;
        }
        return [...prev, option];
      });

      setVeiculoOptions((prev = []) => {
        const mapa = new Map();
        (prev || []).forEach((opt) => {
          const normalizedPrev = normalizeOptionValue(opt);
          if (normalizedPrev) {
            mapa.set(normalizedPrev, opt);
          }
        });
        if (normalizedValue) {
          mapa.set(normalizedValue, option);
        }
        return Array.from(mapa.values());
      });
    },
    [setVeiculoOptions, normalizeOptionValue]
  );

  const toggleRecommendationDetails = useCallback((recommendationId) => {
    setExpandedRecommendations((prev) => {
      const next = new Set(prev);
      if (next.has(recommendationId)) {
        next.delete(recommendationId);
      } else {
        next.add(recommendationId);
      }
      return next;
    });
  }, []);

  const onSubmit = useCallback(async () => {
    if (!initialData) return;
    try {
      setLoading(true);

      const values = {
        ...initialData,
        veiculos: selectedVeiculos || [],
      };
      const payload = mapFormToCreateApi(values);

      if (!id) {
        notification.error("Nao e possivel salvar novo pedido neste modo.");
        return;
      }

      await updatePedido(id, payload);

      const prevIds = new Set(
        (initialVeiculosRef.current || [])
          .map((o) => normalizeOptionValue(o))
          .filter(Boolean)
      );
      const novos = (selectedVeiculos || []).filter(
        (o) => !prevIds.has(normalizeOptionValue(o))
      );
      if (novos.length > 0) {
        const pedidoIdNumber = Number(id);
        const idPedidoPayload = Number.isNaN(pedidoIdNumber) ? id : pedidoIdNumber;
        await Promise.all(
          novos.map((o) => {
            const rawVehicleId = extractOptionId(o);
            if (rawVehicleId === null) {
              return undefined;
            }
            const vehicleIdNumber = Number(rawVehicleId);
            const vehicleIdParam = Number.isNaN(vehicleIdNumber) ? rawVehicleId : vehicleIdNumber;
            return relacionarVeiculoPedido(vehicleIdParam, idPedidoPayload, false);
          })
        );
      }

      notification.success("Pedido atualizado com sucesso!");
      history.goBack();
    } catch (error) {
      // tratados pelo interceptor global
    } finally {
      setLoading(false);
    }
  }, [id, history, initialData, selectedVeiculos, normalizeOptionValue, extractOptionId]);
  if ((isReadOnly || isEditMode) && loadingData) {
    return (
      <Card>
        <CardBody>
          <div style={{ textAlign: "center", padding: 50 }}>Carregando dados do pedido...</div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card style={{ borderRadius: 16, boxShadow: "0 3px 8px rgba(0,0,0,0.1)" }}>
      <CardBody style={{ padding: "24px 32px" }}>
        <h4 style={{ marginBottom: 20, fontWeight: 700, color: "#333" }}>Detalhes do Pedido</h4>

        <div style={{ borderTop: "2px solid #eee", marginTop: 24, paddingTop: 24 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: 6,
            }}
          >
            <h5 style={{ fontWeight: 700, color: "#333", marginBottom: 0 }}>Frotas</h5>
            <button
              type="button"
              onClick={openRulesModal}
              title="Ver regras de recomendacao das frotas"
              aria-label="Ver regras de recomendacao das frotas"
              style={{
                border: "none",
                background: "none",
                padding: 0,
                marginLeft: 8,
                cursor: "pointer",
                color: "#007bff",
                display: "inline-flex",
                alignItems: "center",
                fontSize: 18,
              }}
            >
              <span className="lnr lnr-question-circle" aria-hidden="true" />
            </button>
          </div>
          <p style={{ color: "#666", marginBottom: 12 }}>Busque e selecione as frotas para este pedido:</p>

          <RecommendationSection
            loading={loadingRecomendacoes}
            recommendationCards={recommendationCards}
            pedidoRecomendado={pedidoRecomendado}
            selectedRecommendationIds={selectedRecommendationIds}
            expandedRecommendations={expandedRecommendations}
            onToggleDetails={toggleRecommendationDetails}
            onSelectRecommendation={handleSelectRecommendation}
            extractOptionId={extractOptionId}
            normalizeOptionValue={normalizeOptionValue}
          />

          <VehicleSearch
            onSearch={handleSearchVeiculos}
            veiculoOptions={veiculoOptions}
            selectedVeiculos={selectedVeiculos}
            toggleSelecionado={toggleSelecionado}
            normalizeOptionValue={normalizeOptionValue}
          />

          <SelectedVeiculosPanel
            selectedVeiculos={selectedVeiculos}
            selectedVeiculosCount={selectedVeiculosCount}
            normalizeOptionValue={normalizeOptionValue}
          />

          <CapacitySummary
            selectedVeiculosCount={selectedVeiculosCount}
            capacidadeTotalSelecionada={capacidadeTotalSelecionada}
            pesoDoPedidoNumber={pesoDoPedidoNumber}
            capacityFeedback={capacityFeedback}
            formatDecimalPtBr={formatDecimalPtBr}
          />
          <Modal show={isRulesModalOpen} onHide={closeRulesModal} centered size="lg">
            <Modal.Header closeButton>
              <Modal.Title>Regras Atuais de Recomendacao</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p style={{ color: "#555", marginBottom: 16 }}>
                Essas sao as regras de negocio usadas para aprovar ou reprovar as recomendacoes deste pedido.
              </p>
              <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                {RECOMMENDATION_RULES.map((rule) => (
                  <li key={rule.id} style={{ marginBottom: 12 }}>
                    <strong>{rule.title}:</strong> {rule.description}
                  </li>
                ))}
              </ul>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" onClick={closeRulesModal}>
                Entendi
              </Button>
            </Modal.Footer>
          </Modal>
        </div>        <Bloco titulo="Identificação">
          <Linha label="Número do Pedido" value={fmt(valuesForView?.numeroDoPedido)} />
          <Linha label="Carga" value={fmt(valuesForView?.carga?.label)} />
          <Linha label="Condição do Pedido" value={fmt(valuesForView?.condicaoDoPedido)} />
          <Linha label="Observação" value={fmt(valuesForView?.observacaoPedido)} />
        </Bloco>

        <Bloco titulo="Locais">
          <Linha label="Origem" value={fmt(valuesForView?.origem)} />
          <Linha label="Destino" value={fmt(valuesForView?.destino)} />
          <Linha label="Local de Palletização" value={fmt(valuesForView?.localPalletizacao)} />
          <Linha label="Empresa/Filial" value={fmt(valuesForView?.empresaFilial)} />
          <Linha label="Tomador" value={fmt(valuesForView?.tomador)} />
          <Linha label="Rota" value={fmt(valuesForView?.rota)} />
        </Bloco>

        <Bloco titulo="Participantes">
          <Linha label="Remetente" value={fmt(valuesForView?.remetente)} />
          <Linha label="Destinatário" value={fmt(valuesForView?.destinatario)} />
          <Linha label="Tipo de Remetente" value={fmt(valuesForView?.tipoDeRemetente)} />
          <Linha label="Pedido Embarcador" value={fmt(valuesForView?.numeroPedidoEmbarcador)} />
        </Bloco>

        <Bloco titulo="Carga & Veículo">
          <Linha label="Tipo de Carga" value={fmt(valuesForView?.tipoDeCarga)} />
          <Linha label="Tipo de Operação" value={fmt(valuesForView?.tipoDeOperacao)} />
          <Linha label="Modelo Veicular Solicitado" value={fmt(valuesForView?.modeloVeicularSolicitado)} />
          <Linha label="Nº de Pallets" value={fmt(valuesForView?.numeroDePallets)} />
          <Linha label="Peso do Pedido" value={fmt(valuesForView?.pesoDoPedido)} />
          <Linha label="Peso Líquido" value={fmt(valuesForView?.pesoLiquido)} />
          <Linha label="Qtd. Entregas" value={fmt(valuesForView?.quantidadeDeEntrega)} />
        </Bloco>

        <Bloco titulo="Datas">
          <Linha label="Agendamento" value={fmtDate(valuesForView?.dataAgendamento)} />
          <Linha label="Agendamento do Pedido" value={fmtDate(valuesForView?.dataAgendamentoPedido)} />
          <Linha label="Data Coleta" value={fmtDate(valuesForView?.dataColeta)} />
          <Linha label="Previsão Saída" value={fmtDate(valuesForView?.dataPrevisaoSaida)} />
          <Linha label="Prev. Entrega Remetente" value={fmtDate(valuesForView?.dataPrevisaoEntregaRemetente)} />
          <Linha label="Inicial Viagem Executada" value={fmtDate(valuesForView?.dataInicialViagemExecutada)} />
          <Linha label="Final Viagem Executada" value={fmtDate(valuesForView?.dataFinalViagemExecutada)} />
          <Linha label="Inicial Viagem Faturada" value={fmtDate(valuesForView?.dataInicialViagemFaturada)} />
          <Linha label="Final Viagem Faturada" value={fmtDate(valuesForView?.dataFinalViagemFaturada)} />
        </Bloco>

        {/* Botões */}
        {!isReadOnly && isEditMode && (
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <ButtonWithLoading variant="primary" onClick={onSubmit} loading={loading ? "true" : undefined}>
              Atualizar
            </ButtonWithLoading>
            <Button variant="secondary" onClick={() => history.goBack()}>
              Cancelar
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default Forms;




